import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI, invitationAPI } from '../services/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activated, setActivated] = useState(false);

  const loadUser = useCallback(async () => {
    const token = localStorage.getItem('access_token');
    const savedUser = localStorage.getItem('user');

    if (token && savedUser) {
      try {
        const parsed = JSON.parse(savedUser);
        setUser(parsed);
        setActivated(parsed.activated || parsed.role === 'admin' || parsed.role === 'teacher');
        setLoading(false); // Optimistically stop loading indicator so the UI renders immediately

        // Verify token is still valid in the background
        const response = await authAPI.getMe();
        const freshUser = response.data.user;
        setUser(freshUser);
        setActivated(freshUser.activated || freshUser.role === 'admin' || freshUser.role === 'teacher');
        localStorage.setItem('user', JSON.stringify(freshUser));
      } catch (error) {
        console.error('Token validation failed:', error);
        logout();
      }
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    loadUser();
    // Listen for profile updates from Profile page
    const onStorage = () => {
      const saved = localStorage.getItem('user');
      if (saved) {
        try {
          const parsed = JSON.parse(saved);
          setUser(parsed);
        } catch { /* ignore */ }
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [loadUser]);

  const login = (accessToken, refreshToken, userData) => {
    localStorage.setItem('access_token', accessToken);
    localStorage.setItem('refresh_token', refreshToken);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    // On fresh login, students always see InvitationGate to pick a group.
    // Admins bypass it. Page refresh uses loadUser() which reads DB state.
    setActivated(userData.role === 'admin' || userData.role === 'teacher');
  };

  const logout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
    setUser(null);
    setActivated(false);
  };

  const activateAccount = async (code) => {
    const res = await invitationAPI.activate(code);
    const freshUser = res.data.user;
    setUser(freshUser);
    setActivated(true);
    localStorage.setItem('user', JSON.stringify(freshUser));
    return res.data;
  };

  const selectCode = async (code) => {
    const res = await invitationAPI.selectCode(code);
    const freshUser = res.data.user;
    setUser(freshUser);
    setActivated(true);
    localStorage.setItem('user', JSON.stringify(freshUser));
    return res.data;
  };

  const isAdmin = user?.role === 'admin';
  const isTeacher = user?.role === 'teacher';
  const isStudent = user?.role === 'student';

  return (
    <AuthContext.Provider value={{
      user, loading, login, logout, isAdmin, isTeacher, isStudent,
      activated, activateAccount, selectCode,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
